import React, { useEffect, useState } from 'react';
import { Mic, MicOff, ShieldAlert, Check } from 'lucide-react';
import { apiService, AudioConsent } from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';

/**
 * Audit-konformer Status-Indikator + Schalter fuer den Audio-Aufnahme-Consent
 * eines Patienten. Pflicht-Vorstufe zu jeder Aufnahme (Art. 9 DSGVO).
 *
 * Persistiert in `intake_forms.audio_recording_consent` per
 * `setAudioConsent`. Wenn kein IntakeForm vorhanden: Backend legt ein
 * minimales an.
 */

interface Props {
    patientId: number;
    onChange?: (granted: boolean | null) => void;
}

const AudioConsentBadge: React.FC<Props> = ({ patientId, onChange }) => {
    const { addToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [consent, setConsent] = useState<AudioConsent | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const res = await apiService.getAudioConsent(patientId);
                if (cancelled) return;
                setConsent(res.data.data.audio_recording_consent);
                onChange?.(res.data.data.audio_recording_consent.granted);
            } catch (e: any) {
                if (cancelled) return;
                setConsent(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [patientId]);

    const update = async (granted: boolean) => {
        try {
            setBusy(true);
            const res = await apiService.setAudioConsent(patientId, granted);
            const next = res.data.data.audio_recording_consent;
            setConsent(next);
            onChange?.(next.granted);
            addToast(granted ? 'Audio-Einwilligung erfasst.' : 'Audio-Einwilligung entzogen.', 'success');
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Fehler beim Speichern der Einwilligung', 'error');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-brand-text-muted">
                <Mic className="w-3.5 h-3.5 opacity-50" />
                Audio-Consent lädt …
            </span>
        );
    }

    const granted = consent?.granted === true;
    const denied = consent?.granted === false;
    const pristine = consent?.granted === null || !consent;

    return (
        <div className="inline-flex items-center gap-2 text-xs">
            {granted && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200" title={consent?.granted_at ? `Erteilt: ${new Date(consent.granted_at).toLocaleString('de-DE')}` : undefined}>
                    <Check className="w-3 h-3" />
                    Audio-Consent erteilt
                </span>
            )}
            {denied && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                    <MicOff className="w-3 h-3" />
                    Audio verweigert
                </span>
            )}
            {pristine && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    <ShieldAlert className="w-3 h-3" />
                    Audio-Consent nicht erfasst
                </span>
            )}
            {!granted ? (
                <button
                    type="button"
                    onClick={() => update(true)}
                    disabled={busy}
                    className="px-2 py-0.5 text-[11px] font-medium text-brand-primary hover:bg-brand-primary/10 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    Erteilen
                </button>
            ) : (
                <button
                    type="button"
                    onClick={() => update(false)}
                    disabled={busy}
                    className="px-2 py-0.5 text-[11px] font-medium text-brand-text-muted hover:text-red-600 hover:bg-red-50 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    Entziehen
                </button>
            )}
        </div>
    );
};

export default AudioConsentBadge;
